/**
 * Client-side image preprocessing for chat photo uploads.
 *
 * Why this exists:
 *   Anthropic and OpenAI vision APIs downscale anything longer than
 *   ~1568px on the long edge before charging for tokens, so uploading
 *   the 4032px iPhone original wastes bandwidth for the user and
 *   storage for us without any quality benefit. Re-encoding as WebP at
 *   quality 0.85 typically shaves another ~60% off the JPEG original.
 *
 *   EXIF metadata is dropped in the process (canvas re-encode never
 *   carries EXIF forward). We explicitly apply orientation before
 *   drawing so portrait photos don't come out sideways.
 *
 * When to skip:
 *   - HEIC / HEIF: browsers can't always decode these (Safari can,
 *     Chrome can't). Let the server handle the conversion rather than
 *     half-breaking the upload.
 *   - Already small (≤ MAX_DIMENSION on long edge AND under SIZE_THRESHOLD):
 *     re-encoding would just add latency with no measurable win.
 *   - GIF: animations would become single frames, which is surprising.
 *     Leave as-is.
 *   - Preprocess failure (decode error, canvas.toBlob returns null):
 *     fall back to the original file silently so the user's upload
 *     still works.
 */

const MAX_DIMENSION = 1568; // Anthropic vision long-edge cap
const WEBP_QUALITY = 0.85;
const MIN_DIMENSION = 400; // below this, vision can't read detail — reject
const SIZE_THRESHOLD_BYTES = 500 * 1024; // skip preprocess for small files

const SKIP_TYPES = new Set(["image/heic", "image/heif", "image/gif"]);

/**
 * Preprocess an image File before upload.
 * Returns a new File if the image was resized/re-encoded, or the original
 * File if preprocessing was skipped or failed.
 *
 * Throws only for hard-rejects (too-small image). Soft failures fall
 * back to the original file.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function preprocessImage(file) {
    if (!file || !file.type) return file;

    const mime = file.type.toLowerCase();

    // Formats we don't process client-side
    if (SKIP_TYPES.has(mime)) return file;

    // Small files: not worth the decode round-trip
    if (file.size <= SIZE_THRESHOLD_BYTES) return file;

    let bitmap;
    try {
        // createImageBitmap handles orientation automatically in modern
        // browsers (Safari 14+, Chrome 90+). Fallback path below covers
        // older browsers via <img>.
        if (typeof createImageBitmap === "function") {
            bitmap = await createImageBitmap(file, {
                imageOrientation: "from-image",
            });
        } else {
            bitmap = await decodeViaImageElement(file);
        }
    } catch (err) {
        // Decode failed — maybe HEIC on a browser that claimed to support
        // it, or a corrupt file. Let the server try.
        console.warn("[imagePreprocess] decode failed, uploading original:", err);
        return file;
    }

    try {
        const { width, height } = bitmap;

        if (width < MIN_DIMENSION && height < MIN_DIMENSION) {
            // Too small to be useful — the vision model can't read detail
            // at thumbnail resolution. Reject so the user retakes rather
            // than silently getting a bad quote.
            const err = new Error(
                `Image is too small (${width}×${height}). ` +
                `Please upload one where the long edge is at least ${MIN_DIMENSION}px.`,
            );
            err.code = "IMAGE_TOO_SMALL";
            throw err;
        }

        const longEdge = Math.max(width, height);
        const scale = longEdge > MAX_DIMENSION ? MAX_DIMENSION / longEdge : 1;
        const targetW = Math.round(width * scale);
        const targetH = Math.round(height * scale);

        const canvas = typeof OffscreenCanvas !== "undefined"
            ? new OffscreenCanvas(targetW, targetH)
            : Object.assign(document.createElement("canvas"), {
                width: targetW,
                height: targetH,
            });

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            console.warn("[imagePreprocess] no 2d context, uploading original");
            return file;
        }
        ctx.drawImage(bitmap, 0, 0, targetW, targetH);

        const outType = supportsWebpEncode() ? "image/webp" : "image/jpeg";
        const blob = await canvasToBlob(canvas, outType, WEBP_QUALITY);

        if (!blob) {
            console.warn("[imagePreprocess] toBlob returned null, uploading original");
            return file;
        }

        // If the "optimised" version is somehow larger than the source,
        // keep the source. Happens on tiny already-compressed images.
        if (blob.size >= file.size) return file;

        const newExt = outType === "image/webp" ? ".webp" : ".jpg";
        const baseName = file.name.replace(/\.[^.]+$/, "") || "photo";
        return new File([blob], `${baseName}${newExt}`, {
            type: outType,
            lastModified: Date.now(),
        });
    } finally {
        if (bitmap && typeof bitmap.close === "function") bitmap.close();
    }
}

function decodeViaImageElement(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (err) => {
            URL.revokeObjectURL(url);
            reject(err);
        };
        img.src = url;
    });
}

function supportsWebpEncode() {
    // Feature-sniff: create a 1px canvas and ask it to encode as WebP.
    // If the browser ignores the type, it'll fall back to PNG and the
    // returned data URL won't start with the WebP prefix.
    try {
        const c = document.createElement("canvas");
        c.width = 1;
        c.height = 1;
        return c.toDataURL("image/webp").startsWith("data:image/webp");
    } catch {
        return false;
    }
}

function canvasToBlob(canvas, type, quality) {
    // OffscreenCanvas uses convertToBlob; HTMLCanvasElement uses toBlob.
    if (typeof canvas.convertToBlob === "function") {
        return canvas.convertToBlob({ type, quality });
    }
    return new Promise((resolve) => {
        canvas.toBlob(resolve, type, quality);
    });
}
